import {PlaceOrderSide} from '@px/enum';
import {storageUtil} from '@px/helper/storage';
import {polyfillGridItems} from '@px/calc/grid-layout';

const STORAGE_KEY = 'contract_grid_layout';

export const defaultGridLayoutBreakpoints = createDefaultGridLayoutBreakpoints();

export function readGridLayoutBreakpoint(range: number): GridLayoutBreakpoint {
  const gridLayoutConfig = readGridLayoutConfig();
  return gridLayoutConfig.breakpoints.find((o) => o.range === range) || createDefaultGridLayoutBreakpoint(PlaceOrderSide.Right, range);
}

export function writeGridLayoutBreakpoint(gridLayoutBreakpoint: GridLayoutBreakpoint) {
  const {version, breakpoints} = readGridLayoutConfig();
  const newBreakpoints = calcNewBreakpoints(gridLayoutBreakpoint, breakpoints);
  writeGridLayoutConfig(version, newBreakpoints);
}

function calcNewBreakpoints(gridLayoutBreakpoint: GridLayoutBreakpoint, breakpoints: GridLayoutBreakpoint[]) {
  const {range, cols, items} = gridLayoutBreakpoint;
  const oldIndex = breakpoints.findIndex((o) => o.range === range);
  const newIndex = oldIndex < 0 ? breakpoints.length : oldIndex;
  const deleteCount = oldIndex < 0 ? 0 : 1;
  const newBreakpoints = [...breakpoints];
  newBreakpoints.splice(newIndex, deleteCount, {range, cols, items});
  newBreakpoints.sort((a, b) => a.range - b.range);
  return newBreakpoints;
}

function readGridLayoutConfig(): GridLayoutConfig {
  return storageUtil.readStorageObject(STORAGE_KEY, createDefaultGridLayoutConfig());
}

function writeGridLayoutConfig(version: number, breakpoints: GridLayoutBreakpoint[]) {
  storageUtil.writeStorageObject(STORAGE_KEY, {version, breakpoints});
}

function createDefaultGridLayoutConfig(): GridLayoutConfig {
  return {
    version: 2,
    breakpoints: [],
  };
}

function createDefaultGridLayoutBreakpoint(placeOrderSide: PlaceOrderSide, range: number): GridLayoutBreakpoint {
  const gridLayoutBreakpoint = defaultGridLayoutBreakpoints.find((o) => o.range === range);
  if (gridLayoutBreakpoint) {
    return {
      range: gridLayoutBreakpoint.range,
      cols: gridLayoutBreakpoint.cols,
      items: polyfillGridItems(gridLayoutBreakpoint.createDefaultItems(placeOrderSide)),
    };
  }
  return null;
}

function createDefaultGridLayoutBreakpoints() {
  return [
    {
      range: 768,
      cols: 24,
      createDefaultItems(placeOrderSide: PlaceOrderSide): GridItem[] {
        return [];
      },
    },
    {
      range: 1024,
      cols: 24,
      createDefaultItems(placeOrderSide: PlaceOrderSide): GridItem[] {
        if (placeOrderSide === PlaceOrderSide.Left) {
          return [
            {x: 0, y: 0, w: 24, h: 10, id: 'ChartView', min: {w: 8, h: 6}, max: {w: 24, h: 33}},
            {x: 0, y: 10, w: 24, h: 8, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 0, y: 18, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        if (placeOrderSide === PlaceOrderSide.Right) {
          return [
            {x: 0, y: 0, w: 24, h: 10, id: 'ChartView', min: {w: 8, h: 6}, max: {w: 24, h: 33}},
            {x: 0, y: 10, w: 24, h: 8, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 0, y: 18, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        return [];
      },
    },
    {
      range: 1280,
      cols: 24,
      createDefaultItems(placeOrderSide: PlaceOrderSide): GridItem[] {
        if (placeOrderSide === PlaceOrderSide.Left) {
          return [
            {x: 0, y: 0, w: 8, h: 14, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 8, y: 0, w: 16, h: 14, id: 'ChartView', min: {w: 8, h: 6}, max: {w: 24, h: 33}},
            {x: 0, y: 14, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        if (placeOrderSide === PlaceOrderSide.Right) {
          return [
            {x: 0, y: 0, w: 16, h: 14, id: 'ChartView', min: {w: 8, h: 6}, max: {w: 24, h: 33}},
            {x: 16, y: 0, w: 8, h: 14, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 0, y: 14, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        return [];
      },
    },
    {
      // range: 1440,
      range: 1536,
      cols: 24,
      createDefaultItems(placeOrderSide: PlaceOrderSide): GridItem[] {
        if (placeOrderSide === PlaceOrderSide.Left) {
          return [
            {x: 0, y: 0, w: 6, h: 15, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 6, y: 0, w: 18, h: 15, id: 'ChartView', min: {w: 6, h: 6}, max: {w: 24, h: 34}},
            {x: 0, y: 15, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        if (placeOrderSide === PlaceOrderSide.Right) {
          return [
            {x: 0, y: 0, w: 18, h: 15, id: 'ChartView', min: {w: 6, h: 6}, max: {w: 24, h: 33}},
            {x: 18, y: 0, w: 6, h: 15, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 0, y: 15, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        return [];
      },
    },
    {
      range: 1920,
      cols: 24,
      createDefaultItems(placeOrderSide: PlaceOrderSide): GridItem[] {
        if (placeOrderSide === PlaceOrderSide.Left) {
          return [
            {x: 0, y: 0, w: 5, h: 15, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 5, y: 0, w: 19, h: 15, id: 'ChartView', min: {w: 6, h: 6}, max: {w: 24, h: 34}},
            {x: 0, y: 15, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        if (placeOrderSide === PlaceOrderSide.Right) {
          return [
            {x: 0, y: 0, w: 19, h: 15, id: 'ChartView', min: {w: 6, h: 6}, max: {w: 24, h: 33}},
            {x: 19, y: 0, w: 5, h: 15, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 0, y: 15, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        return [];
      },
    },
    {
      range: 2560,
      cols: 24,
      createDefaultItems(placeOrderSide: PlaceOrderSide): GridItem[] {
        if (placeOrderSide === PlaceOrderSide.Left) {
          return [
            {x: 0, y: 0, w: 4, h: 16, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 4, y: 0, w: 20, h: 16, id: 'ChartView', min: {w: 6, h: 6}, max: {w: 24, h: 34}},
            {x: 0, y: 16, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        if (placeOrderSide === PlaceOrderSide.Right) {
          return [
            {x: 0, y: 0, w: 20, h: 16, id: 'ChartView', min: {w: 6, h: 6}, max: {w: 24, h: 33}},
            {x: 20, y: 0, w: 4, h: 16, id: 'BookTradesView', min: {w: 4, h: 4}, max: {w: 24, h: 33}},
            {x: 0, y: 16, w: 24, h: 18, id: 'PositionOrderTabView'},
          ];
        }
        return [];
      },
    },
  ];
}
